import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import moment from 'moment';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular, BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import AccessRequired from '../components/AccessRequired';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import IconHeader from '@shared/ui/molecules/IconHeader';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { useTranslation } from 'react-i18next';
import TextField from '@shared/ui/atoms/TextField';
import { GenericDropDownList } from '@shared/ui/molecules/GenericDropDownList';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import Colours from '@shared/ui/atoms/Colours';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { AuditLogEntry, requestAuditLogs } from '@shared/services/AuditLogService';

// Mirrors AuditActionCategory in the API; UNKNOWN is not offered as a filter.
const ACTIONS_BY_CATEGORY: { [category: string]: string[] } = {
  HOLIDAYS: [
    'BOOK_HOLIDAY', 'APPROVE_HOLIDAY', 'PENDING_HOLIDAY', 'REJECT_HOLIDAY', 'CANCELLED_HOLIDAY_REQUEST',
    'BOOK_CUSTOM_HOLIDAY', 'APPROVE_CUSTOM_HOLIDAY', 'PENDING_CUSTOM_HOLIDAY', 'REJECT_CUSTOM_HOLIDAY',
    'CANCELLED_CUSTOM_WHEREABOUTS_APPROVALS_REQUEST', 'UPDATE_USERS_HOLIDAY_ALLOWANCE', 'PERFORM_ROLL_OVER',
  ],
  WHEREABOUTS: ['UPDATED_WHEREABOUTS'],
  USER_MANAGEMENT: [
    'CREATE_USER', 'UPDATE_USER', 'DELETE_USER', 'PERMANENTLY_DELETE_USER', 'RESTORE_USER',
    'USER_REGISTERED', 'UPDATE_USER_PERMISSIONS',
    'CREATE_GROUP', 'UPDATE_GROUP', 'DELETE_GROUP', 'PASSWORD_RESET_REQUESTED', 'PASSWORD_RESET_COMPLETED',
  ],
  MEETING_ROOMS: [
    'CREATE_MEETING_ROOM', 'DELETE_MEETING_ROOM', 'CREATE_MEETING_ROOM_OUTLOOK_EVENT', 'UPDATE_MEETING_ROOM_OUTLOOK_EVENT',
    'CANCEL_MEETING_ROOM_BOOKING',
  ],
  COMPANY_SETTINGS: [
    'UPDATE_PLATFORM_CONFIG', 'UPDATE_COMPANY_SETTINGS', 'UPDATE_TENANT_LINK', 'UPDATE_DOMAIN_SIGNUP',
  ],
  SECURITY: ['GENERATE_API_KEY', 'REVOKE_API_KEY', 'UPDATE_API_KEY_LABEL'],
  NOTIFICATIONS: ['TEAMS_NOTIFICATION_SENT'],
  OFFICE_CHECK_IN: ['AUTOMATIC_OFFICE_CHECK_IN', 'ADD_CHECKIN_IP_ADDRESS', 'REMOVE_CHECKIN_IP_ADDRESS'],
};

const CATEGORIES = Object.keys(ACTIONS_BY_CATEGORY);
const ALL_ACTIONS = CATEGORIES.flatMap(c => ACTIONS_BY_CATEGORY[c]);
const SOURCES = ['WEB', 'SCIM', 'SCHEDULER', 'PUBLIC_API', 'SYSTEM'];

const NO_AFFECTED_USER = 'NONE';

// Fallback for enum values that don't have a translation yet
function prettify(value: string): string {
  const lower = value.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function AuditLogsPage() {
  const current = useSelector(selectCurrentUser);
  const isCompanyAdmin = useMemo(() => isCompanyAdminUser(current), [current]);
  const {t} = useTranslation();

  const actionLabel = useCallback((a: string) => t(`audit-logs-page.actions.${a}`, prettify(a)), [t]);
  const categoryLabel = useCallback((c: string) => t(`audit-logs-page.categories.${c}`, prettify(c)), [t]);
  const sourceLabel = useCallback((s: string) => t(`audit-logs-page.sources.${s}`, prettify(s)), [t]);

  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');
  const [source, setSource] = useState('');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');

  // Debounce typing so each keystroke doesn't fire a request
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchText.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchText]);

  const selectCategory = (newCategory: string) => {
    setCategory(newCategory);
    // A selected action outside the new category would silently override it
    if (newCategory && action && !ACTIONS_BY_CATEGORY[newCategory].includes(action)) {
      setAction('');
    }
  };

  const load = useCallback(async (pageToLoad: number, append: boolean) => {
    setLoading(true);
    setError(false);
    try {
      const response = await requestAuditLogs({page: pageToLoad, action, category, source, search});
      setEntries(prev => append ? [...prev, ...response.entries] : response.entries);
      setPage(response.page);
      setHasMore(response.hasMore);
      setTotalEntries(response.totalEntries);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [action, category, source, search]);

  // Any filter change restarts from the first page and replaces the list
  useEffect(() => {
    if (!isCompanyAdmin) return;
    load(0, false);
  }, [isCompanyAdmin, load]);

  const categoryItems = useMemo(() => [
    {id: '', name: t('audit-logs-page.all-categories')},
    ...CATEGORIES.map(c => ({id: c, name: categoryLabel(c)})),
  ], [t, categoryLabel]);

  const actionItems = useMemo(() => {
    const actions = category ? ACTIONS_BY_CATEGORY[category] : ALL_ACTIONS;
    return [
      {id: '', name: t('audit-logs-page.all-actions')},
      ...actions.map(a => ({id: a, name: actionLabel(a)})),
    ];
  }, [t, category, actionLabel]);

  const sourceItems = useMemo(() => [
    {id: '', name: t('audit-logs-page.all-sources')},
    ...SOURCES.map(s => ({id: s, name: sourceLabel(s)})),
  ], [t, sourceLabel]);

  return (
    <PageWrapper className="management">
      <GoBackButton/>
      <IconHeader icon={IconTypes.Search} style={{marginBottom: 32}} />
      <HeadlineLarge className="management__title">{t('audit-logs-page.title')}</HeadlineLarge>
      <AccessRequired visible={!isCompanyAdmin} companyAdminsRequired={true} teamAdminsRequired={false}/>

      {isCompanyAdmin && <>
        <FiltersRow>
          <SearchWrapper>
            <TextField dataTest="auditLogSearch"
                       label="audit-logs-page.search"
                       placeholder="audit-logs-page.search-placeholder"
                       value={searchText}
                       onChange={setSearchText}/>
          </SearchWrapper>
          <DropDownWrapper>
            <GenericDropDownList label="audit-logs-page.category"
                                 placeholder="audit-logs-page.all-categories"
                                 items={categoryItems}
                                 selectedId={category}
                                 onSelect={selectCategory}/>
          </DropDownWrapper>
          <DropDownWrapper>
            <GenericDropDownList label="audit-logs-page.action"
                                 placeholder="audit-logs-page.all-actions"
                                 items={actionItems}
                                 selectedId={action}
                                 onSelect={setAction}/>
          </DropDownWrapper>
          <DropDownWrapper>
            <GenericDropDownList label="audit-logs-page.source"
                                 placeholder="audit-logs-page.all-sources"
                                 items={sourceItems}
                                 selectedId={source}
                                 onSelect={setSource}/>
          </DropDownWrapper>
        </FiltersRow>

        {error && <BodyRegular colour={Colours.red}>{t('audit-logs-page.error')}</BodyRegular>}

        {!error && <BodySmall colour={Colours.darkGrey} style={{marginBottom: 8}}>
          {t('audit-logs-page.showing', {shown: entries.length, total: totalEntries})}
        </BodySmall>}

        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-date')}</BodyVerySmall></th>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-action')}</BodyVerySmall></th>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-user')}</BodyVerySmall></th>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-affected')}</BodyVerySmall></th>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-source')}</BodyVerySmall></th>
                <th><BodyVerySmall weight={600} colour={Colours.darkGrey}>{t('audit-logs-page.col-description')}</BodyVerySmall></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} data-test="auditLogEntry">
                  <DateCell><BodyVerySmall colour={Colours.darkGrey}>{moment(entry.createdAt).format('DD MMM YYYY, HH:mm')}</BodyVerySmall></DateCell>
                  <td><ActionPill><BodyVerySmall weight={600}>{actionLabel(entry.action)}</BodyVerySmall></ActionPill></td>
                  <td><BodySmall>{entry.actioningUserName}</BodySmall></td>
                  <td><BodySmall>{entry.affectedUserId === NO_AFFECTED_USER ? '—' : entry.affectedUserName}</BodySmall></td>
                  <td><BodyVerySmall colour={Colours.darkGrey}>{entry.source ? sourceLabel(entry.source) : '—'}</BodyVerySmall></td>
                  <SummaryCell><BodySmall colour={Colours.darkGrey}>{entry.summary ?? ''}</BodySmall></SummaryCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>

        {!loading && !error && entries.length === 0 && <BodyRegular colour={Colours.darkGrey}>{t('audit-logs-page.no-results')}</BodyRegular>}

        {loading && <SpinnerWrapper>
          <LoadingSpinner hideBorder={true} hideText={true}/>
        </SpinnerWrapper>}

        {!loading && hasMore && <LoadMoreWrapper>
          <OutlineButton dataTest="auditLogLoadMore" text="audit-logs-page.load-more" click={() => load(page + 1, true)}/>
        </LoadMoreWrapper>}
      </>}
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 16px auto 0 auto;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  @media (${tablet}) {
    margin: 32px auto 0 auto;
    padding: 48px 64px;
  }
`

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin: 24px 0;
`

const SearchWrapper = styled.div`
  flex: 2;
  min-width: 220px;
`

const DropDownWrapper = styled.div`
  flex: 1;
  min-width: 180px;
`

const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  th {
    padding: 8px 12px;
    border-bottom: 2px solid ${Colours.veryLightGrey};
    white-space: nowrap;
    text-transform: uppercase;
  }

  td {
    padding: 12px;
    vertical-align: top;
    border-bottom: 1px solid ${Colours.veryLightGrey};
  }
`

const DateCell = styled.td`
  white-space: nowrap;
`

const SummaryCell = styled.td`
  min-width: 280px;
`

const ActionPill = styled.span`
  display: inline-block;
  background-color: ${Colours.veryLightGrey};
  border-radius: 12px;
  padding: 2px 10px;
  white-space: nowrap;
`

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px 0;
`
